<script>
  import { onMount, onDestroy } from "svelte";
  import { lang } from '../lib/stores';
  import { select } from "d3-selection";
  import { scaleLog } from "d3-scale";
  import { extent } from "d3";
  export let data = [];
  export let songs = {};
  export let albums = [];
  export let annotations = [];
  export let subject = "Parole";
  export let verb = "Usate";
  export let minRecurrence = 10;
  export let ticks = [minRecurrence, 50, 100, 200, "max"];
  export let tooltipSubject = "La parola";
  export let tooltipVerb = "stata usata";

  let width = 0;
  let height = 0;
  let svgObject;
  let svgWidth = 0;
  let svgHeight = 0;

  let selectedLang;
	const unsubscribe = lang.subscribe((value) => selectedLang = value);

  const tooltip = {
    text: selectedLang === 'it' ? "Seleziona un punto per vedere i dettagli" : "Select a dot to see the details",
  };

  let radius = 6;
  const padding = 2;

  const margins = [30, 8, 30, 8];

  const dodge = (data, radius, x) => {
    const radius2 = radius ** 2;
    const circles = data
      .map((d) => ({ x: x(d.count), data: d }))
      .sort((a, b) => a.x - b.x);
    const epsilon = 1e-3;
    let head = null,
      tail = null;
    // Returns true if circle ⟨x,y⟩ intersects with any circle in the queue.
    function intersects(x, y) {
      let a = head;
      while (a) {
        if (radius2 - epsilon > (a.x - x) ** 2 + (a.y - y) ** 2) {
          return true;
        }
        a = a.next;
      }
      return false;
    }
    // Place each circle sequentially.
    for (const b of circles) {
      // Remove circles from the queue that can’t intersect the new circle b.
      while (head && head.x < b.x - radius2) head = head.next;
      // Choose the minimum non-intersecting tangent.
      if (intersects(b.x, (b.y = 0))) {
        let a = head;
        b.y = Infinity;
        do {
          let y1 = a.y + Math.sqrt(radius2 - (a.x - b.x) ** 2);
          let y2 = a.y - Math.sqrt(radius2 - (a.x - b.x) ** 2);
          if (Math.abs(y1) < Math.abs(b.y) && !intersects(b.x, y1)) b.y = y1;
          if (Math.abs(y2) < Math.abs(b.y) && !intersects(b.x, y2)) b.y = y2;
          a = a.next;
        } while (a);
      }
      // Add b to the queue.
      b.next = null;
      if (head === null) head = tail = b;
      else tail = tail.next = b;
    }
    return circles;
  };

  const getSize = () => {
    if (window.matchMedia("screen and (min-width: 768px").matches) {
      return "l";
    }
    return "s";
  };

  const showTooltip = (point) => {
    const old = svgObject.querySelector("circle.highlighted");
    if (old != null) {
      old.classList.remove("highlighted");
    }
    svgObject
      .querySelector(`#term-${point.data.term}`)
      .classList.add("highlighted");
    const currentAlbums = [
      ...new Set(
        point.data.songs.map((d) => songs[d]).map((d) => albums[d.album]),
      ),
    ];
    const albumsBlock = currentAlbums.map(
      (d) =>
        `<img loading="lazy" src="${d.cover}" alt="${d.title}" width="12" height="12" class="beeswarm-cover" /> ${d.title}`,
    );
    if (selectedLang === 'it') {
      tooltip.text = `<p class="beeswarm-tooltip-text">${tooltipSubject}: <em>${point.data.term}</em> è ${tooltipVerb} <em class="data count">${point.data.count} volte</em> ${albumsBlock.length > 1 ? "negli " : "nell'"}album: ${albumsBlock.join(", ")}.</p>`;
    } else {
      tooltip.text = `<p class="beeswarm-tooltip-text">${tooltipSubject}: <em>${point.data.term}</em> ${tooltipVerb} <em class="data count">${point.data.count} times</em> in: ${albumsBlock.join(", ")}.</p>`;
    }
  };

  const draw = () => {
    svgWidth = width;
    svgHeight = height;
    const size = getSize();
    radius = 6;
    margins[1] = margins[3] = radius;
    if (size === "s") {
      radius = 5;
      margins[1] = margins[3] = radius;
    }
    if (data.length > 0) {
      const chartData = data;
      const svg = select(svgObject);
      svg.selectAll("g").remove();

      const innerWidth = svgWidth - margins[1] - margins[3];
      const innerHeight = svgHeight - margins[0] - margins[2];
      const dataExtent = extent(chartData, (d) => d.count);
      if (dataExtent[0] > minRecurrence) {
        dataExtent[0] = minRecurrence;
      }
      const x = scaleLog().rangeRound([0, innerWidth]).domain(dataExtent);

      const gGrid = svg
        .append("g")
        .attr("transform", `translate(${margins[3]},${margins[0]})`);

      // Block for Axis and grid
      {
        const gAxis = svg
          .append("g")
          .attr("transform", `translate(${margins[3]},${margins[0]})`);
        // Top Axis
        gAxis
          .append("line")
          .attr("x1", 0)
          .attr("x2", innerWidth)
          .attr("y1", 0)
          .attr("y2", 0)
          .attr("stroke", "black")
          .style("stroke", "var(--foreground)")
          .attr("stroke-width", 1)
          .style("stroke-width", 1)
          .attr("class", "beeswarm-axis-line");
        gAxis
          .append("text")
          .attr("x", 0)
          .attr("y", 0)
          .attr("dy", 5)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "hanging")
          .attr("class", "beeswarm-axis-text data")
          .text(size == "s" ? `← - ${verb}` : `← ${subject} ${selectedLang === 'it' ? "meno" : "less"} ${verb}`);
        gAxis
          .append("text")
          .attr("x", innerWidth)
          .attr("y", 0)
          .attr("dy", 5)
          .attr("text-anchor", "end")
          .attr("dominant-baseline", "hanging")
          .attr("class", "beeswarm-axis-text data")
          .text(size == "s" ? `+ ${verb} →` : `${subject} ${selectedLang === 'it' ? "più" : "more"} ${verb} →`);
        gAxis
          .append("text")
          .attr("x", innerWidth / 2)
          .attr("y", 0)
          .attr("dy", 5)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "hanging")
          .attr("class", "beeswarm-axis-text data")
          .text(`${selectedLang === 'it' ? "Frequenza di utilizzo" : "Occurrences"}`);
        // Bottom Axis
        gAxis
          .append("line")
          .attr("x1", 0)
          .attr("x2", innerWidth)
          .attr("y1", innerHeight)
          .attr("y2", innerHeight)
          .attr("stroke", "black")
          .style("stroke", "var(--foreground)")
          .attr("stroke-width", 1)
          .style("stroke-width", 1)
          .attr("class", "beeswarm-axis-line");
        gAxis
          .append("text")
          .attr("x", 0)
          .attr("y", innerHeight)
          .attr("dy", -5)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "auto")
          .attr("class", "beeswarm-axis-text data")
          .text(size == "s" ? `← - ${verb}` : `← ${subject} ${selectedLang === 'it' ? "meno" : "less"} ${verb}`);
        gAxis
          .append("text")
          .attr("x", innerWidth)
          .attr("y", innerHeight)
          .attr("dy", -5)
          .attr("text-anchor", "end")
          .attr("dominant-baseline", "auto")
          .attr("class", "beeswarm-axis-text data")
          .text(size == "s" ? `+ ${verb} →` : `${subject} ${selectedLang === 'it' ? "più" : "more"} ${verb} →`);
        gAxis
          .append("text")
          .attr("x", innerWidth / 2)
          .attr("y", innerHeight)
          .attr("dy", -5)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "auto")
          .attr("class", "beeswarm-axis-text data")
          .text(`${selectedLang === 'it' ? "Frequenza di utilizzo" : "Occurrences"}`);
        ticks.forEach((t, i) => {
          let val = t;
          if (t === "max") {
            val = t === "max" ? dataExtent[1] : t;
          } else if (dataExtent[1] - t < 25) {
            val = null;
          }
          if (val) {
            gAxis
              .append("line")
              .attr("x1", x(val))
              .attr("x2", x(val))
              .attr("y1", 0)
              .attr("y2", -5)
              .attr("stroke", "black")
              .style("stroke", "var(--foreground)")
              .attr("stroke-width", 1)
              .style("stroke-width", 1)
              .attr("class", "beeswarm-axis-line");
            gAxis
              .append("text")
              .attr("x", x(val))
              .attr("y", 0)
              .attr("dy", -10)
              .attr("fill", "black")
              .style("fill", "var(--foreground)")
              .attr("class", "beeswarm-axis-text data")
              .attr("text-anchor", t === "max" ? "end" : "start")
              .attr("dominant-baseline", "auto")
              .text(i === 0 ? `${verb} ${val} ${selectedLang === 'it' ? "volte" : "times"}` : val);
            gAxis
              .append("line")
              .attr("x1", x(val))
              .attr("x2", x(val))
              .attr("y1", innerHeight)
              .attr("y2", innerHeight + 5)
              .attr("stroke", "black")
              .style("stroke", "var(--foreground)")
              .attr("stroke-width", 1)
              .style("stroke-width", 1)
              .attr("class", "beeswarm-axis-line");
            gAxis
              .append("text")
              .attr("x", x(val))
              .attr("y", innerHeight)
              .attr("dy", 10)
              .attr("fill", "black")
              .style("fill", "var(--foreground)")
              .attr("class", "beeswarm-axis-text data")
              .attr("text-anchor", t === "max" ? "end" : "start")
              .attr("dominant-baseline", "hanging")
              .text(i === 0 ? `${verb} ${val} ${selectedLang === 'it' ? "volte" : "times"}` : val);
            // Grid
            gGrid
              .append("line")
              .attr("x1", x(val))
              .attr("x2", x(val))
              .attr("y1", 0)
              .attr("y2", innerHeight)
              .attr("stroke", "black")
              .style("stroke", "var(--grey)")
              .attr("stroke-width", 1)
              .style("stroke-width", 1)
              .attr("stroke-dasharray", "1 2")
              .style("stroke-dasharray", "1 2")
              .attr("class", "beeswarm-grid-line");
          }
        });
      }

      const circlesContainer = svg
        .append("g")
        .attr("transform", `translate(${margins[3]},${margins[0]})`)
        .attr("class", "beeswarm-data-container");

      const circles = circlesContainer
        .selectAll("circle")
        .data(dodge(chartData, radius * 2 + padding, x))
        .join("circle")
        .attr("cx", (d) => d.x)
        .attr("cy", (d) => innerHeight / 2 + d.y)
        .attr("r", radius)
        .attr("data-value", (d) => d.data.count)
        .attr("id", (d) => `term-${d.data.term}`)
        .attr("class", (d) => `beeswarm-point beeswarm-value-${d.data.count}`)
        .attr("fill", "black")
        .attr("stroke", "none")
        .on("click", (e, d) => showTooltip(d))
        .style("fill", "var(--accent-2)");

      const anntns = svg
        .append("g")
        .attr("transform", `translate(${margins[3]},${margins[0]})`)
        .attr("class", "beeswarm-annotations");
      annotations
        .sort((a, b) => a.count - b.count)
        .forEach((a, i) => {
          const xPos = x(a.count);
          const circlesObj = [];
          const circles = circlesContainer
            .selectAll(`circle[data-value="${a.count}"]`)
            .nodes();
          circles.forEach((c) => {
            circlesObj.push(c.getAttribute("cy"));
          });
          const bottomCirclePosition = Math.max(...circlesObj);
          const topCirclePosition = Math.min(...circlesObj);
          const textAnchor = (() => {
            if (xPos > innerWidth * 0.7) return "end";
            if (xPos < innerWidth * 0.1) return "start";
            return "middle";
          })();
          const yPos =
            i % 2 === 0 ? topCirclePosition - 40 : bottomCirclePosition + 40;
          const arrowYPos =
            i % 2 === 0 ? topCirclePosition - 26 : bottomCirclePosition + 26;
          const imageYPos =
            i % 2 === 0 ? topCirclePosition - 86 : bottomCirclePosition + 46;
          const imageXPos = xPos - 20;
          const arrow = i % 2 === 0 ? "↓" : "↑";

          anntns
            .append("text")
            .attr("x", xPos)
            .attr("y", yPos)
            .attr("class", "data beeswarm-annotation")
            .attr("text-anchor", textAnchor)
            .attr("dominant-baseline", "middle")
            .text(`${a.label}`);
          anntns
            .append("text")
            .attr("x", xPos)
            .attr("y", arrowYPos)
            .attr("class", "data beeswarm-annotation")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .text(arrow);
          if (a.image) {
            anntns
              .append("image")
              .attr("x", imageXPos)
              .attr("y", imageYPos)
              .attr("class", "beeswarm-image")
              .attr("href", a.image)
              .attr("width", 40)
              .attr("height", 40);
          }
        });
    }
  };

  onDestroy(unsubscribe);
  onMount(() => {
    draw();
  });

  $: data, draw();
  $: albums, draw();
  $: songs, draw();
  $: width, draw();
  $: selectedLang, draw();
</script>

<section bind:clientWidth={width} bind:clientHeight={height} class="beeswarm">
  <svg
    bind:this={svgObject}
    width={svgWidth}
    height={svgHeight}
    viewBox={`0 0 ${svgWidth} ${svgHeight}`}
    class="chart-svg-beeswarm"
  />
  <div class="beeswarm-tooltip">
    <div class="beeswarm-tooltip-inner">
      {@html tooltip.text}
    </div>
  </div>
</section>

<style>
  .beeswarm {
    display: block;
    height: 600px;
    position: relative;
    width: 100%;
  }

  .beeswarm-tooltip {
    background: var(--grey-dirtywhite);
    border-bottom: 1px dashed var(--foreground);
    border-top: 1px dashed var(--foreground);
    bottom: 50px;
    /* box-shadow: 0 0 20px 1px var(--grey-light); */
    display: block;
    font-family: var(--data-font-family);
    font-size: 12px;
    left: 0;
    min-width: 300px;
    padding: 1rem 0.5rem;
    position: absolute;
    right: 0;
    width: auto;
    z-index: 9;
  }

  :global(.beeswarm-tooltip .beeswarm-cover) {
    height: 14px;
    vertical-align: middle;
    width: 14px;
  }

  @media screen and (min-width: 768px) {
    .beeswarm-tooltip {
      bottom: 3rem;
      left: auto;
      right: 0.5rem;
    }
  }

  .beeswarm-tooltip-inner {
    align-items: center;
    align-content: stretch;
    justify-content: space-between;
  }

  :global(.beeswarm-tooltip p) {
    font-family: var(--data-font-family);
    font-size: 12px;
  }

  :global(.beeswarm-tooltip em.highlight) {
    color: var(--background);
    text-shadow:
      2px 0 0 var(--accent-2),
      -2px 0 0 var(--accent-2);
  }

  @media screen and (min-width: 1024px) {
    .beeswarm-tooltip {
      width: 30vw;
    }
  }
</style>
